const React = require('react');
const { createElement: h, useRef, useState, useEffect } = React;

// Separate component so each instance gets its own stable random timing via useRef
function AgentIcon(props) {
  var size = props.size || 24;
  var strokeWidth = props.strokeWidth || 1.5;
  var isDragging = props.isDragging || false;
  var nodeStatus = props.nodeStatus || null; // 'running', 'completed', null
  var isWorkflowRunning = props.isWorkflowRunning || false;
  var eyeLookDir = props.eyeLookDir || null; // { x, y } normalized direction

  var containerRef = useRef(null);
  var rafRef = useRef(null);
  var isDraggingRef = useRef(isDragging);
  isDraggingRef.current = isDragging;

  var [eyeOffset, setEyeOffset] = useState({ x: 0, y: 0 });
  var [isHovered, setIsHovered] = useState(false);
  var [dragShaking, setDragShaking] = useState(false);
  var prevDraggingRef = useRef(false);

  var timingRef = useRef(null);
  if (timingRef.current === null) {
    // Negative delay = start mid-cycle, giving each instance a unique phase
    timingRef.current = {
      blinkDur:  (2.2 + Math.random() * 1.2).toFixed(2),   // 2.2–3.4s
      blinkDelay: (-(Math.random() * 3)).toFixed(2),
      scanDur:   (2.6 + Math.random() * 1.4).toFixed(2),   // 2.6–4.0s
      scanDelay:  (-(Math.random() * 4)).toFixed(2)
    };
  }
  var t = timingRef.current;

  // Drag start -> close eyes; drag end -> shake 1s then stop
  useEffect(function() {
    if (isDragging && !prevDraggingRef.current) {
      setDragShaking(true);
    }
    if (!isDragging && prevDraggingRef.current) {
      var timer = setTimeout(function() { setDragShaking(false); }, 1000);
      prevDraggingRef.current = isDragging;
      return function() { clearTimeout(timer); };
    }
    prevDraggingRef.current = isDragging;
  }, [isDragging]);


  // Mouse tracking — attach to the closest node wrapper so eyes follow cursor across the whole node
  useEffect(function() {
    var el = containerRef.current;
    if (!el) return;
    var nodeEl = el.closest('.node-position') || el.closest('.wf-node');
    if (!nodeEl) return;

    function onEnter() { setIsHovered(true); }
    function onLeave() {
      setIsHovered(false);
      setEyeOffset({ x: 0, y: 0 });
    }
    function onMove(e) {
      if (isDraggingRef.current) return;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(function() {
        rafRef.current = null;
        if (!containerRef.current) return;
        var rect = containerRef.current.getBoundingClientRect();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var dx = e.clientX - cx;
        var dy = e.clientY - cy;
        var dist = Math.sqrt(dx * dx + dy * dy);
        var maxR = 120;
        var factor = Math.min(dist / maxR, 1);
        var angle = Math.atan2(dy, dx);
        setEyeOffset({
          x: Math.cos(angle) * factor * 5,
          y: Math.sin(angle) * factor * 3.5
        });
      });
    }

    nodeEl.addEventListener('mouseenter', onEnter);
    nodeEl.addEventListener('mouseleave', onLeave);
    nodeEl.addEventListener('mousemove', onMove);

    return function() {
      nodeEl.removeEventListener('mouseenter', onEnter);
      nodeEl.removeEventListener('mouseleave', onLeave);
      nodeEl.removeEventListener('mousemove', onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Determine animation mode
  // Keep looking at connected node as long as the whole workflow is still running
  var trackingMouse = isHovered && !isDragging && !dragShaking;
  var isExecuting = (nodeStatus === 'running' || (nodeStatus === 'completed' && isWorkflowRunning)) && !isDragging && !dragShaking && !trackingMouse;

  // Build styles per mode
  var eyeStyle, scanStyle;

  if (isDragging) {
    // Dragging: eyes squeezed shut, no movement
    eyeStyle = {
      transformBox: 'fill-box',
      transformOrigin: '50% 50%',
      transform: 'scaleY(0.05)',
      transition: 'transform 0.15s'
    };
    scanStyle = {};
  } else if (dragShaking) {
    // Just dropped: eyes still shut + shake left-right for 2s
    eyeStyle = {
      transformBox: 'fill-box',
      transformOrigin: '50% 50%',
      transform: 'scaleY(0.05)',
      transition: 'transform 0.15s'
    };
    scanStyle = {
      animation: 'aiEyeShake 0.6s ease-out infinite'
    };
  } else if (trackingMouse) {
    // Hover: eyes follow cursor, keep blinking
    eyeStyle = {
      transformBox: 'fill-box',
      transformOrigin: '50% 50%',
      animation: 'aiEyeBlink ' + t.blinkDur + 's ' + t.blinkDelay + 's linear infinite'
    };
    scanStyle = {
      transform: 'translate(' + eyeOffset.x.toFixed(2) + 'px, ' + eyeOffset.y.toFixed(2) + 'px)',
      transition: 'transform 0.06s linear'
    };
  } else if (isExecuting) {
    // Look toward the actual connected node direction, fallback to left/right
    var lookX = eyeLookDir ? eyeLookDir.x * 5 : (nodeStatus === 'running' ? -4 : 4);
    var lookY = eyeLookDir ? eyeLookDir.y * 3.5 : 0;
    eyeStyle = {
      transformBox: 'fill-box',
      transformOrigin: '50% 50%',
      animation: 'aiEyeBlink ' + t.blinkDur + 's ' + t.blinkDelay + 's linear infinite'
    };
    scanStyle = {
      transform: 'translate(' + lookX.toFixed(2) + 'px, ' + lookY.toFixed(2) + 'px)',
      transition: 'transform 0.4s ease-out'
    };
  } else {
    // Idle: original scan + blink
    eyeStyle = {
      transformBox: 'fill-box',
      transformOrigin: '50% 50%',
      animation: 'aiEyeBlink ' + t.blinkDur + 's ' + t.blinkDelay + 's linear infinite'
    };
    scanStyle = {
      animation: 'aiEyeScan ' + t.scanDur + 's ' + t.scanDelay + 's linear infinite'
    };
  }

  var keyframes =
    '@keyframes aiEyeBlink{' +
      '0%,83%{transform:scaleY(1)}' +
      '84%,86%{transform:scaleY(.05)}' +
      '87%,91%{transform:scaleY(1)}' +
      '92%,94%{transform:scaleY(.05)}' +
      '95%,100%{transform:scaleY(1)}' +
    '}' +
    '@keyframes aiEyeScan{' +
      '0%,14%{transform:translateX(0)}' +
      '15%,47%{transform:translateX(-2px)}' +
      '48%,62%{transform:translateX(0)}' +
      '63%,77%{transform:translateX(2px)}' +
      '78%,100%{transform:translateX(0)}' +
    '}' +
    '@keyframes aiEyeShake{' +
      '0%{transform:translateX(0) rotate(0)}' +
      '10%{transform:translateX(-3px) rotate(-4deg)}' +
      '20%{transform:translateX(2.5px) rotate(3deg)}' +
      '30%{transform:translateX(-2px) rotate(-3deg)}' +
      '42%{transform:translateX(1.8px) rotate(2.5deg)}' +
      '55%{transform:translateX(-1.2px) rotate(-1.5deg)}' +
      '68%{transform:translateX(0.8px) rotate(1deg)}' +
      '80%{transform:translateX(-0.4px) rotate(-0.5deg)}' +
      '100%{transform:translateX(0) rotate(0)}' +
    '}';

  return h('span', { ref: containerRef, style: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center' } },
    h('svg', {
      width: Math.round(size * 1.05),
      height: Math.round(size * 0.78),
      viewBox: '0 0 32 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: strokeWidth
    },
      h('style', null, keyframes),
      h('rect', { x: 0.5, y: 3, width: 31, height: 18, rx: 5 }),
      h('g', { style: scanStyle },
        h('rect', { x: 9,  y: 7.5, width: 3, height: 9, rx: 1.5, fill: 'currentColor', stroke: 'none', style: eyeStyle }),
        h('rect', { x: 20, y: 7.5, width: 3, height: 9, rx: 1.5, fill: 'currentColor', stroke: 'none', style: eyeStyle })
      )
    )
  );
}

function NodeIcon(props) {
  var type = props.type;
  var size = props.size || 24;
  var strokeWidth = props.strokeWidth || 1.5;
  var common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: strokeWidth
  };

  switch (type) {
    case 'user':
      return h('svg', common,
        h('path', { d: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2' }),
        h('circle', { cx: 12, cy: 7, r: 4 })
      );
    case 'data':
      return h('svg', common,
        h('rect', { x: 2, y: 3, width: 20, height: 14, rx: 2 }),
        h('line', { x1: 8, y1: 21, x2: 16, y2: 21 }),
        h('line', { x1: 12, y1: 17, x2: 12, y2: 21 })
      );
    case 'validation':
      return h('svg', common,
        h('polyline', { points: '16 18 22 12 16 6' }),
        h('polyline', { points: '8 6 2 12 8 18' })
      );
    case 'sync':
      return h('svg', common,
        h('polyline', { points: '23 4 23 10 17 10' }),
        h('polyline', { points: '1 20 1 14 7 14' }),
        h('path', { d: 'M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15' })
      );
    case 'condition':
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('path', { d: 'M12 3 21 12 12 21 3 12z' }),
        h('path', { d: 'M12 8v4M12 15.5v.5' })
      );
    case 'start':
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('polygon', { points: '6 4 20 12 6 20 6 4' })
      );
    case 'variables':
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('rect', { x: 5, y: 3, width: 14, height: 18, rx: 2.5 }),
        h('path', { d: 'M9 8h6M9 12h6M9 16h3' })
      );
    case 'set-variable':
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('path', { d: 'M3 12h11' }),
        h('path', { d: 'm10 8 4 4-4 4' }),
        h('rect', { x: 16, y: 4, width: 5, height: 16, rx: 1.5 })
      );
    case 'console-log':
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('polyline', { points: '9 8 5 12 9 16' }),
        h('polyline', { points: '15 8 19 12 15 16' })
      );
    case 'agent':
      return h(AgentIcon, { size: size, strokeWidth: strokeWidth, isDragging: props.isDragging, nodeStatus: props.nodeStatus, isWorkflowRunning: props.isWorkflowRunning, eyeLookDir: props.eyeLookDir });
    case 'computer-use':
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('rect', { x: 2.5, y: 4, width: 19, height: 13, rx: 2 }),
        h('path', { d: 'M8 21h8M12 17v4' })
      );
    case 'tool-agent':
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('rect', { x: 4, y: 7, width: 16, height: 13, rx: 2.5 }),
        h('path', { d: 'M9 7V5a3 3 0 0 1 6 0v2' }),
        h('circle', { cx: 9, cy: 13, r: 1 }),
        h('circle', { cx: 15, cy: 13, r: 1 })
      );
    case 'classifier':
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('path', { d: 'M5 20V10M12 20V4M19 20v-7' })
      );
    case 'router':
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('circle', { cx: 6, cy: 12, r: 2.5 }),
        h('circle', { cx: 18, cy: 6, r: 2.5 }),
        h('circle', { cx: 18, cy: 18, r: 2.5 }),
        h('path', { d: 'M8.2 10.8 15.8 7M8.2 13.2 15.8 17' })
      );
    case 'browser-run':
      return h('svg', common,
        h('rect', { x: 3, y: 4, width: 18, height: 14, rx: 2 }),
        h('line', { x1: 3, y1: 8, x2: 21, y2: 8, strokeLinecap: 'round' }),
        h('circle', { cx: 5.5, cy: 6, r: 0.6 }),
        h('circle', { cx: 7.5, cy: 6, r: 0.6 }),
        h('path', { d: 'M11 11l3 5 1-2 2-1-6-2z', strokeLinecap: 'round', strokeLinejoin: 'round' })
      );
    case 'browser-action':
      // Duotone globe + pointer badge. The mask knocks a circular gap out of
      // the globe so the badge reads cleanly on any tile color.
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('mask', { id: 'ni-browser-action-badge', maskUnits: 'userSpaceOnUse', x: 0, y: 0, width: 24, height: 24 },
          h('rect', { x: 0, y: 0, width: 24, height: 24, fill: '#fff' }),
          h('circle', { cx: 17.4, cy: 17.4, r: 5, fill: '#000' })
        ),
        h('g', { mask: 'url(#ni-browser-action-badge)' },
          h('circle', { cx: 12, cy: 12, r: 7.7, fill: 'currentColor', fillOpacity: 0.13 }),
          h('ellipse', { cx: 12, cy: 12, rx: 3.3, ry: 7.7 }),
          h('path', { d: 'M4.3 12h15.4' })
        ),
        h('path', { d: 'M15.6 15.4l4.7 1.8-2.2.8-.8 2.2z', fill: 'currentColor', stroke: 'currentColor', strokeWidth: 1.1 })
      );
    case 'browser-agentic-action':
      // Duotone globe + spark badge — AI-driven browsing.
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('mask', { id: 'ni-browser-agentic-badge', maskUnits: 'userSpaceOnUse', x: 0, y: 0, width: 24, height: 24 },
          h('rect', { x: 0, y: 0, width: 24, height: 24, fill: '#fff' }),
          h('circle', { cx: 17.4, cy: 17.4, r: 5, fill: '#000' })
        ),
        h('g', { mask: 'url(#ni-browser-agentic-badge)' },
          h('circle', { cx: 12, cy: 12, r: 7.7, fill: 'currentColor', fillOpacity: 0.13 }),
          h('ellipse', { cx: 12, cy: 12, rx: 3.3, ry: 7.7 }),
          h('path', { d: 'M4.3 12h15.4' })
        ),
        h('path', { d: 'M17.4 14.8Q18.05 16.75 20 17.4Q18.05 18.05 17.4 20Q16.75 18.05 14.8 17.4Q16.75 16.75 17.4 14.8Z', fill: 'currentColor', stroke: 'none' })
      );
    case 'apify':
      // Fallback glyph only — the node normally renders the Apify logo image
      // (template.logoUrl). A cube/box reads as "run a packaged actor".
      return h('svg', common,
        h('path', { d: 'M12 2 3 7v10l9 5 9-5V7z', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('path', { d: 'M3 7l9 5 9-5M12 12v10', strokeLinecap: 'round', strokeLinejoin: 'round' })
      );
    case 'sleep':
      return h('svg', common,
        h('circle', { cx: 12, cy: 12, r: 9 }),
        h('path', { d: 'M12 7v5l3 3', strokeLinecap: 'round', strokeLinejoin: 'round' })
      );
    case 'loop':
      return h('svg', common,
        h('path', { d: 'M17 2l4 4-4 4', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('path', { d: 'M3 11V9a4 4 0 014-4h14', strokeLinecap: 'round' }),
        h('path', { d: 'M7 22l-4-4 4-4', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('path', { d: 'M21 13v2a4 4 0 01-4 4H3', strokeLinecap: 'round' })
      );
    case 'file-preview':
      return h('svg', common,
        h('path', { d: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('polyline', { points: '14 2 14 8 20 8', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('circle', { cx: 12, cy: 15, r: 2.5 }),
        h('path', { d: 'M7 15c1.5-2.5 8.5-2.5 10 0M7 15c1.5 2.5 8.5 2.5 10 0', strokeLinecap: 'round' })
      );
    case 'on-fail':
      // Life buoy: reads as "rescue/recover" — this node catches a failure
      // from its connected source node instead of letting the run die.
      return h('svg', Object.assign({}, common, { strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('circle', { cx: 12, cy: 12, r: 9 }),
        h('circle', { cx: 12, cy: 12, r: 3.5 }),
        h('path', { d: 'M5.7 5.7l4 4M14.3 14.3l4 4M18.3 5.7l-4 4M9.7 14.3l-4 4' })
      );
    case 'flow-ref':
      // Mirrors the .flw file icon used in the workbook explorer panel:
      // an upward S-curve with a small arrowhead.
      return h('svg', common,
        h('path', { d: 'M3 16 C 8 6, 14 18, 21 6', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('polyline', { points: '15 6 21 6 21 12', strokeLinecap: 'round', strokeLinejoin: 'round' })
      );
    case 'custom':
      // Curly braces { }: reads as "code / custom logic". Two mirrored stroked
      // paths, each a brace that bows out at its vertical midpoint. Sits on the
      // purple custom-node tile (white glyph) per systemObjectTheme.
      return h('svg', common,
        h('path', {
          d: 'M8.5 4.5C7 4.5 7 6 7 7.5 7 9 6.5 12 4.5 12 6.5 12 7 15 7 16.5 7 18 7 19.5 8.5 19.5',
          strokeLinecap: 'round',
          strokeLinejoin: 'round'
        }),
        h('path', {
          d: 'M15.5 4.5C17 4.5 17 6 17 7.5 17 9 17.5 12 19.5 12 17.5 12 17 15 17 16.5 17 18 17 19.5 15.5 19.5',
          strokeLinecap: 'round',
          strokeLinejoin: 'round'
        })
      );
    default:
      return h('svg', common,
        h('circle', { cx: 12, cy: 12, r: 8 })
      );
  }
}

module.exports = NodeIcon;
